import { useState, useCallback, useEffect, useRef } from 'react';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import type { StatusMap } from '../types';
import { TechniqueStatus } from '../types';
import { loadStatusMap, saveStatusMap } from '../utils/storage';
import { isTestMode } from '../testMode';

const STATUS_CYCLE: Record<TechniqueStatus, TechniqueStatus> = {
  [TechniqueStatus.Unchecked]: TechniqueStatus.MarkedForPractice,
  [TechniqueStatus.MarkedForPractice]: TechniqueStatus.Completed,
  [TechniqueStatus.Completed]: TechniqueStatus.Unchecked,
  // Archived isn't part of the tap-cycle; cycling just un-archives it.
  [TechniqueStatus.Archived]: TechniqueStatus.Unchecked,
};

/** Starred, as Favorites counts it: marked for practice or finished. The
 *  tally function (see scheduledStarTally in functions/index.js) uses the
 *  same rule, so a star here is a star there. */
export function isStarred(s: TechniqueStatus | undefined): boolean {
  return s === TechniqueStatus.MarkedForPractice || s === TechniqueStatus.Completed;
}

/** The user's technique statuses (star / done / archived).
 *
 *  Kept in localStorage as before — every read is local and instant — and
 *  mirrored to Firestore at `user_status/{uid}` so a new phone or a cleared
 *  browser gets them back, and so the weekly Most Starred tally has
 *  something to count. Each starred technique carries the time it was
 *  starred, which is what makes "this week" answerable.
 *
 *  Firestore is the source of truth once it exists: on first sight of the
 *  doc the two are merged (this device's stars fill any the doc lacks) and
 *  the merge written back, so nobody's device-only stars are lost in the
 *  move. After that, every change writes the whole map — a merge write
 *  would leave un-starred keys behind. */
export function useTechniqueStatus(uid: string | null) {
  const [statusMap, setStatusMap] = useState<StatusMap>(loadStatusMap);
  // When each starred technique was starred (ms). Only stored, not shown.
  const starredAt = useRef<Record<string, number>>({});
  const synced = useRef(false);

  const ref = uid && !isTestMode ? doc(db, 'user_status', uid) : null;

  useEffect(() => {
    synced.current = false;
    if (!ref) return;
    return onSnapshot(ref, (snap) => {
      if (snap.metadata.hasPendingWrites) return; // our own write echoing back
      const remote = (snap.data()?.status ?? {}) as StatusMap;
      const remoteAt = (snap.data()?.starredAt ?? {}) as Record<string, number>;
      if (!synced.current) {
        synced.current = true;
        // First sight: what's on this device fills in what the doc lacks.
        const local = loadStatusMap();
        const merged: StatusMap = { ...local, ...remote };
        const now = Date.now();
        for (const id of Object.keys(merged)) {
          if (isStarred(merged[id]) && !remoteAt[id]) remoteAt[id] = now;
        }
        starredAt.current = remoteAt;
        saveStatusMap(merged);
        setStatusMap(merged);
        if (!snap.exists() || Object.keys(merged).length !== Object.keys(remote).length) {
          void setDoc(ref, { status: merged, starredAt: remoteAt, updatedAt: serverTimestamp() });
        }
        return;
      }
      starredAt.current = remoteAt;
      saveStatusMap(remote);
      setStatusMap(remote);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  const persist = useCallback((prev: StatusMap, updated: StatusMap) => {
    saveStatusMap(updated);
    if (!ref) return;
    const at = { ...starredAt.current };
    for (const id of Object.keys(updated)) {
      if (isStarred(updated[id]) && !isStarred(prev[id])) at[id] = Date.now();
    }
    for (const id of Object.keys(at)) {
      if (!isStarred(updated[id])) delete at[id];
    }
    starredAt.current = at;
    void setDoc(ref, { status: updated, starredAt: at, updatedAt: serverTimestamp() });
  }, [ref]);

  const cycleStatus = useCallback((techniqueId: string) => {
    setStatusMap((prev) => {
      const current = prev[techniqueId] || TechniqueStatus.Unchecked;
      const next = STATUS_CYCLE[current];
      const updated = { ...prev, [techniqueId]: next };
      // Clean up unchecked entries to keep storage lean
      if (next === TechniqueStatus.Unchecked) {
        delete updated[techniqueId];
      }
      persist(prev, updated);
      return updated;
    });
  }, [persist]);

  const setStatus = useCallback((techniqueId: string, status: TechniqueStatus) => {
    setStatusMap((prev) => {
      const updated = { ...prev, [techniqueId]: status };
      if (status === TechniqueStatus.Unchecked) {
        delete updated[techniqueId];
      }
      persist(prev, updated);
      return updated;
    });
  }, [persist]);

  const reload = useCallback(() => {
    setStatusMap(loadStatusMap());
  }, []);

  return { statusMap, cycleStatus, setStatus, reload };
}
